import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import {
  DEFAULT_MAX_EMAILS_PER_DAY,
  MAX_EMAILS_PER_DAY,
  MIN_EMAILS_PER_DAY,
} from "../../lib/accountSettings";

/**
 * Row with Switch — must be a stable module component so toggles are not
 * unmounted on each parent re-render (which cancels the switch transition).
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {boolean} props.checked
 * @param {function(boolean): void} props.onCheckedChange
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
function NotifSwitchRow({ id, label, description, checked, onCheckedChange, disabled }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="text-text-primary">
          {label}
        </Label>
        {description ? <p className="mt-1 text-sm text-text-secondary">{description}</p> : null}
      </div>
      <Switch id={id} checked={!!checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

/**
 * Daily email cap stepper: − / number / +.
 * Typing is allowed; values clamp to 1–20 on blur or Enter.
 * @param {Object} props
 * @param {string} props.id
 * @param {number} props.value
 * @param {boolean} props.disabled
 * @param {function(number): void} props.onCommit
 * @returns {JSX.Element}
 */
function EmailLimitRow({ id, value, disabled, onCommit }) {
  const [draft, setDraft] = useState(() => String(value ?? DEFAULT_MAX_EMAILS_PER_DAY));

  useEffect(() => {
    setDraft(String(value ?? DEFAULT_MAX_EMAILS_PER_DAY));
  }, [value]);

  const committed = Number.isInteger(value) ? value : DEFAULT_MAX_EMAILS_PER_DAY;

  /**
   * Clamp a typed or stepped value and persist when it differs from the saved cap.
   * @param {*} raw
   */
  const commit = (raw) => {
    const parsed = typeof raw === "number" ? raw : Number.parseInt(String(raw), 10);
    if (!Number.isInteger(parsed)) {
      setDraft(String(committed));
      return;
    }
    const next = Math.min(MAX_EMAILS_PER_DAY, Math.max(MIN_EMAILS_PER_DAY, parsed));
    setDraft(String(next));
    if (next !== committed) {
      onCommit(next);
    }
  };

  const innerDisabledClass = disabled ? "disabled:opacity-100" : undefined;
  const noFocusRing =
    "outline-none ring-0 ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0";

  return (
    <div
      className={cn(
        "inline-flex h-8 items-center overflow-hidden rounded-md border border-ui-border bg-ui-card",
        disabled && "pointer-events-none opacity-50"
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Decrease max emails per day"
        disabled={disabled || committed <= MIN_EMAILS_PER_DAY}
        onClick={() => commit(committed - 1)}
        className={cn(
          "h-8 w-8 shrink-0 rounded-none text-text-secondary hover:bg-ui-muted hover:text-text-primary",
          noFocusRing,
          innerDisabledClass
        )}
      >
        -
      </Button>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        aria-label="Max emails per day"
        disabled={disabled}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value.replace(/\D/g, "").slice(0, 2));
        }}
        onBlur={() => commit(draft)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit(draft);
            event.currentTarget.blur();
          }
        }}
        className={cn(
          "h-8 w-10 rounded-none border-0 border-x border-ui-border bg-transparent px-0 text-center shadow-none tabular-nums",
          noFocusRing,
          innerDisabledClass
        )}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Increase max emails per day"
        disabled={disabled || committed >= MAX_EMAILS_PER_DAY}
        onClick={() => commit(committed + 1)}
        className={cn(
          "h-8 w-8 shrink-0 rounded-none text-text-secondary hover:bg-ui-muted hover:text-text-primary",
          noFocusRing,
          innerDisabledClass
        )}
      >
        +
      </Button>
    </div>
  );
}

const ACTIVITY_ROWS = [
  {
    id: "notif-messages",
    key: "newMessages",
    label: "New messages",
    description: "When someone sends you a chat message",
  },
  {
    id: "notif-mentions",
    key: "mentions",
    label: "Mentions",
    description: "When someone mentions you in a post, comment, or event",
  },
  {
    id: "notif-connection-requests",
    key: "connectionRequests",
    label: "Connection requests",
    description: "When someone wants to connect with you",
  },
  {
    id: "notif-connection-activity",
    key: "connectionActivity",
    label: "Connection activity",
    description: "Posts and events from people you are connected with",
  },
  {
    id: "notif-org",
    key: "organizationUpdates",
    label: "Organization updates",
    description: "Posts and events from organizations you are affiliated with",
  },
  {
    id: "notif-likes",
    key: "postLikes",
    label: "Post likes",
    description: "When someone likes your post",
  },
  {
    id: "notif-comments",
    key: "postComments",
    label: "Post comments",
    description: "When someone comments on your post or replies to your comment",
  },
];

/**
 * @param {Object} props
 * @param {Object} props.notifications
 * @param {function(string, Object): Promise<void>} props.onUpdateSettings
 * @returns {JSX.Element}
 */
function NotificationsTab({ notifications, onUpdateSettings }) {
  const [notif, setNotif] = useState(() => ({ ...notifications }));

  useEffect(() => {
    setNotif({ ...notifications });
  }, [notifications]);

  const handleToggle = (key, value) => {
    setNotif((prev) => ({ ...prev, [key]: value }));
    onUpdateSettings("notifications", { [key]: value });
  };

  const handleMaxEmailsCommit = (next) => {
    setNotif((prev) => ({ ...prev, maxEmailsPerDay: next }));
    onUpdateSettings("notifications", { maxEmailsPerDay: next });
  };

  const emailEnabled = !!notif.email;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Notifications</h2>
        <p className="mt-1 text-sm text-text-secondary sm:text-base">
          Choose which activity emails you receive. Changes save immediately.
        </p>
      </div>

      <Card className="border-ui-border">
        <CardHeader>
          <CardTitle className="text-base text-text-primary">Email</CardTitle>
          <CardDescription>Master switch for activity emails</CardDescription>
        </CardHeader>
        <CardContent className="space-y-0 pt-0">
          <NotifSwitchRow
            id="notif-email"
            label="Email notifications"
            description="Receive activity emails. Invitations and account emails are still sent."
            checked={notif.email}
            onCheckedChange={(v) => handleToggle("email", v)}
          />
        </CardContent>
      </Card>

      <Card className="border-ui-border">
        <CardHeader>
          <CardTitle className="text-base text-text-primary">Max emails per day</CardTitle>
          <CardDescription>
            This will not prevent you from receiving essential emails like password reset and
            account alerts.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <EmailLimitRow
            id="notif-max-emails"
            value={notif.maxEmailsPerDay}
            disabled={!emailEnabled}
            onCommit={handleMaxEmailsCommit}
          />
        </CardContent>
      </Card>

      <Card className="border-ui-border">
        <CardHeader>
          <CardTitle className="text-base text-text-primary">Activity</CardTitle>
          <CardDescription>
            {emailEnabled
              ? "What you are emailed about"
              : "Turn on email notifications above to receive these"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-0 pt-0">
          {ACTIVITY_ROWS.map((row, index) => (
            <React.Fragment key={row.key}>
              {index > 0 ? <Separator /> : null}
              <NotifSwitchRow
                id={row.id}
                label={row.label}
                description={row.description}
                checked={notif[row.key]}
                disabled={!emailEnabled}
                onCheckedChange={(v) => handleToggle(row.key, v)}
              />
            </React.Fragment>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default NotificationsTab;
